import React, { useMemo } from 'react';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { Ship, Plus, Trash2, Plane, Hotel, AlertTriangle } from 'lucide-react';
import DateInput from './DateInput';

const YesNoToggle = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-1.5">
    <Label className="text-xs text-slate-300">{label}</Label>
    <div className="flex items-center gap-1 p-0.5 bg-slate-700/50 rounded-md">
      <button type="button" onClick={() => onChange(true)}
        className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value === true ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}
        data-testid={`${testId}-yes`}>
        Yes
      </button>
      <button type="button" onClick={() => onChange(false)}
        className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value === false ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
        data-testid={`${testId}-no`}>
        No
      </button>
    </div>
  </div>
);

const UkAbroadToggle = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-1 p-0.5 bg-slate-700/50 rounded-md w-fit">
    <button type="button" onClick={() => onChange(false)}
      className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value === false ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
      data-testid={`${testId}-uk`}>
      UK
    </button>
    <button type="button" onClick={() => onChange(true)}
      className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value === true ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
      data-testid={`${testId}-abroad`}>
      Abroad
    </button>
  </div>
);

const HotelFields = ({ prefix, formData, setFormData, dateContext }) => (
  <div className="space-y-2 pl-3 border-l-2 border-amber-500/30">
    <div className="flex items-center gap-2">
      <Hotel className="h-3.5 w-3.5 text-amber-400" />
      <p className="text-[11px] text-slate-400">Where was the hotel?</p>
      <UkAbroadToggle
        value={formData[`${prefix}_hotel_abroad`]}
        onChange={(val) => setFormData({ ...formData, [`${prefix}_hotel_abroad`]: val })}
        testId={`${prefix}-hotel-location`}
      />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <input value={formData[`${prefix}_hotel_name`] || ''} onChange={(e) => setFormData({ ...formData, [`${prefix}_hotel_name`]: e.target.value })} placeholder="Hotel name"
        className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid={`${prefix}-hotel-name`} />
      <input value={formData[`${prefix}_hotel_location`] || ''} onChange={(e) => setFormData({ ...formData, [`${prefix}_hotel_location`]: e.target.value })} placeholder="Location"
        className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid={`${prefix}-hotel-location-input`} />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-0.5">
        <p className="text-[10px] text-slate-500">Check-in</p>
        <DateInput contextDate={dateContext} value={formData[`${prefix}_hotel_checkin`] || ''} onChange={(e) => setFormData({ ...formData, [`${prefix}_hotel_checkin`]: e.target.value })}
          className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid={`${prefix}-hotel-checkin`} />
      </div>
      <div className="space-y-0.5">
        <p className="text-[10px] text-slate-500">Check-out</p>
        <DateInput contextDate={dateContext} value={formData[`${prefix}_hotel_checkout`] || ''} onChange={(e) => setFormData({ ...formData, [`${prefix}_hotel_checkout`]: e.target.value })}
          className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid={`${prefix}-hotel-checkout`} />
      </div>
    </div>
  </div>
);

const DateConflictWarnings = ({ formData }) => {
  const warnings = useMemo(() => {
    const w = [];
    const vesselJoin = formData.date_joined_vessel;
    const vesselLeft = formData.date_left_vessel;

    if (formData.pre_hotel_checkout && vesselJoin && formData.pre_hotel_checkout > vesselJoin) {
      w.push(`Hotel check-out (${formData.pre_hotel_checkout}) is after vessel join date (${vesselJoin}).`);
    }
    if (formData.post_hotel_checkin && vesselLeft && formData.post_hotel_checkin < vesselLeft) {
      w.push(`Post-trip hotel check-in (${formData.post_hotel_checkin}) is before vessel leave date (${vesselLeft}).`);
    }
    return w;
  }, [formData.date_joined_vessel, formData.date_left_vessel, formData.pre_hotel_checkout, formData.post_hotel_checkin]);

  if (warnings.length === 0) return null;
  return (
    <div className="p-2 bg-amber-500/10 rounded border border-amber-500/30 space-y-1" data-testid="date-conflict-warning">
      {warnings.map((msg) => (
        <div key={msg} className="flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-300">{msg}</p>
        </div>
      ))}
    </div>
  );
};

const WorkTripFields = ({ formData, setFormData, ukCrossings, addUkCrossing, updateUkCrossing, removeUkCrossing, showVesselFields = true, dateContext = '' }) => {
  const status = formData.uk_waters_status || 'outside';

  return (
    <>
      {/* Vessel Details - only show when showVesselFields is true (Step 1) */}
      {showVesselFields && (
        <div className="space-y-4 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Ship className="h-4 w-4 text-blue-400" />
            <h3 className="text-sm font-medium text-blue-400">Work Assignment Details</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Vessel Name *</Label>
              <input value={formData.vessel_name} onChange={(e) => setFormData({ ...formData, vessel_name: e.target.value })} placeholder="e.g., MV Ocean Star" className="flex h-10 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-3 py-2 text-sm" required data-testid="vessel-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contractor</Label>
              <input value={formData.contractor} onChange={(e) => setFormData({ ...formData, contractor: e.target.value })} placeholder="e.g., Maritime Services Ltd" className="flex h-10 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-3 py-2 text-sm" data-testid="contractor-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-slate-300">Date Joined Vessel *</Label><DateInput contextDate={dateContext} value={formData.date_joined_vessel} onChange={(e) => setFormData({ ...formData, date_joined_vessel: e.target.value })} className="bg-slate-900 border-slate-600 text-white" required data-testid="date-joined-vessel-input" /></div>
            <div className="space-y-2"><Label className="text-slate-300">Date Left Vessel *</Label><DateInput contextDate={dateContext} value={formData.date_left_vessel} onChange={(e) => setFormData({ ...formData, date_left_vessel: e.target.value })} className="bg-slate-900 border-slate-600 text-white" required data-testid="date-left-vessel-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-slate-300">Port of Joining</Label><input value={formData.port_joining} onChange={(e) => setFormData({ ...formData, port_joining: e.target.value })} placeholder="e.g., Rotterdam" className="flex h-10 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-3 py-2 text-sm" data-testid="port-joining-input" /></div>
            <div className="space-y-2"><Label className="text-slate-300">Port of Leaving</Label><input value={formData.port_leaving} onChange={(e) => setFormData({ ...formData, port_leaving: e.target.value })} placeholder="e.g., Singapore" className="flex h-10 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-3 py-2 text-sm" data-testid="port-leaving-input" /></div>
          </div>
          <div className="space-y-2"><Label className="text-slate-300">Foreign Ports Visited</Label><input value={formData.foreign_ports} onChange={(e) => setFormData({ ...formData, foreign_ports: e.target.value })} placeholder="e.g., Dubai, Hong Kong, Tokyo (comma separated)" className="flex h-10 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-3 py-2 text-sm" data-testid="foreign-ports-input" /></div>
        </div>
      )}
      
      {/* === Step 2: Dates & Travel (wizard mode) === */}
      {!showVesselFields && (
        <>
          {/* --- PRE-TRIP Q&A --- */}
          <div className="space-y-2 p-3 rounded-lg border border-slate-700/50 bg-slate-800/50">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Before Joining Vessel</p>

            {/* Q1: Travel */}
            <YesNoToggle
              label="Did you travel to join the vessel?"
              value={formData.pre_had_travel}
              onChange={(val) => {
                const updates = { pre_had_travel: val };
                if (!val) {
                  updates.pre_travel_abroad = undefined;
                  updates.pre_departure_date = '';
                  updates.pre_travel_route = '';
                }
                setFormData({ ...formData, ...updates });
              }}
              testId="pre-travel"
            />

            {formData.pre_had_travel === true && (
              <>
                {/* Q1a: Travel outside UK? */}
                <YesNoToggle
                  label="Was the travel outside the UK?"
                  value={formData.pre_travel_abroad}
                  onChange={(val) => {
                    const updates = { pre_travel_abroad: val };
                    if (!val) updates.pre_departure_date = '';
                    setFormData({ ...formData, ...updates });
                  }}
                  testId="pre-travel-abroad"
                />

                {/* Date Left UK — only shown if travel was outside UK */}
                {formData.pre_travel_abroad === true && (
                  <div className="space-y-1 pl-3 border-l-2 border-amber-500/30">
                    <Label className="text-amber-400 text-xs font-medium">Date Left UK</Label>
                    <DateInput contextDate={dateContext} value={formData.pre_departure_date || ''} onChange={(e) => setFormData({ ...formData, pre_departure_date: e.target.value })}
                      className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid="pre-departure-date-input" />
                  </div>
                )}

                {/* Travel route — shown for all travel */}
                <div className="pl-3 border-l-2 border-amber-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Plane className="h-3 w-3 text-amber-400" />
                    <p className="text-[10px] text-slate-500">Travel route</p>
                  </div>
                  <input value={formData.pre_travel_route || ''} onChange={(e) => setFormData({ ...formData, pre_travel_route: e.target.value })} placeholder="e.g., Home to Heathrow, fly to Rotterdam"
                    className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid="pre-travel-route" />
                </div>
              </>
            )}

            {/* Q2: Hotel */}
            <YesNoToggle
              label="Did you stay in a hotel before joining?"
              value={formData.pre_had_hotel}
              onChange={(val) => {
                const updates = { pre_had_hotel: val };
                if (!val) {
                  updates.pre_hotel_abroad = undefined;
                  updates.pre_hotel_name = '';
                  updates.pre_hotel_location = '';
                  updates.pre_hotel_checkin = '';
                  updates.pre_hotel_checkout = '';
                }
                setFormData({ ...formData, ...updates });
              }}
              testId="pre-hotel"
            />

            {formData.pre_had_hotel === true && (
              <HotelFields prefix="pre" formData={formData} setFormData={setFormData} dateContext={dateContext} />
            )}
          </div>

          {/* --- VESSEL DATES (always shown) --- */}
          <div className="space-y-3 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Ship className="h-4 w-4 text-blue-400" /><h3 className="text-xs font-medium text-blue-400">Vessel Dates</h3></div>
              <div className="text-xs text-slate-500">{formData.vessel_name}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Date Joined Vessel *</Label>
                <DateInput contextDate={dateContext} value={formData.date_joined_vessel} onChange={(e) => setFormData({ ...formData, date_joined_vessel: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" required data-testid="date-joined-vessel-input" />
                {(status === 'outside' || status === 'sailed_into_uk') && formData.pre_had_travel !== true && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <Checkbox id="no_uk_departure" checked={formData.no_uk_departure} onCheckedChange={(checked) => setFormData({ ...formData, no_uk_departure: checked })} className="border-cyan-500 data-[state=checked]:bg-cyan-500 h-3 w-3" data-testid="no-uk-departure-checkbox" />
                    <Label htmlFor="no_uk_departure" className="text-[11px] text-cyan-400 cursor-pointer">Already abroad</Label>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Date Left Vessel *</Label>
                <DateInput contextDate={dateContext} value={formData.date_left_vessel} onChange={(e) => setFormData({ ...formData, date_left_vessel: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" required data-testid="date-left-vessel-input" />
                {(status === 'outside' || status === 'sailed_from_uk') && formData.post_had_travel !== true && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <Checkbox id="no_uk_return" checked={formData.no_uk_return} onCheckedChange={(checked) => setFormData({ ...formData, no_uk_return: checked })} className="border-cyan-500 data-[state=checked]:bg-cyan-500 h-3 w-3" data-testid="no-uk-return-checkbox" />
                    <Label htmlFor="no_uk_return" className="text-[11px] text-cyan-400 cursor-pointer">Stayed abroad</Label>
                  </div>
                )}
              </div>
            </div>

            {/* UK Water Crossing dates */}
            {status === 'sailed_from_uk' && (
              <div className="space-y-1 pt-1 border-t border-blue-500/20">
                <Label className="text-amber-400 text-xs">Date Left UK *</Label>
                <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="date-left-uk-input" />
              </div>
            )}
            {status === 'sailed_into_uk' && (
              <div className="space-y-1 pt-1 border-t border-blue-500/20">
                <Label className="text-amber-400 text-xs">Date Entered UK *</Label>
                <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="date-returned-uk-input" />
              </div>
            )}
            {status === 'both' && (
              <div className="space-y-2 pt-1 border-t border-blue-500/20">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-amber-400 text-xs">Date Left UK *</Label><DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="date-left-uk-input" /></div>
                  <div className="space-y-1"><Label className="text-amber-400 text-xs">Date Returned to UK *</Label><DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white h-9" data-testid="date-returned-uk-input" /></div>
                </div>
                {ukCrossings && ukCrossings.map((crossing, idx) => (
                  <div key={crossing._key || `crossing-${idx}`} className="grid grid-cols-2 gap-3 p-2 bg-slate-900/30 rounded border border-amber-500/30" data-testid={`crossing-leg-${idx + 2}`}>
                    <div className="space-y-1"><div className="flex items-center justify-between"><Label className="text-slate-300 text-xs">Left UK (Leg {idx + 2})</Label><button type="button" onClick={() => removeUkCrossing(idx)} className="text-red-400 hover:text-red-300" data-testid={`remove-crossing-${idx + 2}`}><Trash2 className="h-3 w-3" /></button></div><DateInput contextDate={dateContext} value={crossing.date_left_uk_waters} onChange={(e) => updateUkCrossing(idx, 'date_left_uk_waters', e.target.value)} className="bg-slate-900 border-slate-600 text-white h-9" data-testid={`crossing-${idx + 2}-left`} /></div>
                    <div className="space-y-1"><Label className="text-slate-300 text-xs">Returned (Leg {idx + 2})</Label><DateInput contextDate={dateContext} value={crossing.date_returned_uk_waters} onChange={(e) => updateUkCrossing(idx, 'date_returned_uk_waters', e.target.value)} className="bg-slate-900 border-slate-600 text-white h-9" data-testid={`crossing-${idx + 2}-returned`} /></div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addUkCrossing} className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 border-dashed h-7 text-xs" data-testid="add-extra-leg-btn"><Plus className="h-3 w-3 mr-1" /> Add Extra Leg</Button>
              </div>
            )}

            {(formData.no_uk_departure || formData.no_uk_return) && (
              <div className="p-1.5 bg-cyan-500/10 rounded border border-cyan-500/30">
                <p className="text-[11px] text-cyan-400">
                  <strong>Continuous Trip:</strong>{' '}
                  {formData.no_uk_departure && !formData.no_uk_return && "You were already abroad before this trip."}
                  {!formData.no_uk_departure && formData.no_uk_return && "You stayed abroad after this trip."}
                  {formData.no_uk_departure && formData.no_uk_return && "This entire trip was part of a continuous period abroad."}
                </p>
              </div>
            )}

            <DateConflictWarnings formData={formData} />
          </div>

          {/* --- POST-TRIP Q&A --- */}
          <div className="space-y-2 p-3 rounded-lg border border-slate-700/50 bg-slate-800/50">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">After Leaving Vessel</p>

            {/* Q1: Travel */}
            <YesNoToggle
              label="Did you travel after leaving the vessel?"
              value={formData.post_had_travel}
              onChange={(val) => {
                const updates = { post_had_travel: val };
                if (!val) {
                  updates.post_travel_abroad = undefined;
                  updates.post_return_date = '';
                  updates.post_travel_route = '';
                }
                setFormData({ ...formData, ...updates });
              }}
              testId="post-travel"
            />

            {formData.post_had_travel === true && (
              <>
                {/* Q1a: Travel outside UK? */}
                <YesNoToggle
                  label="Was the travel outside the UK?"
                  value={formData.post_travel_abroad}
                  onChange={(val) => {
                    const updates = { post_travel_abroad: val };
                    if (!val) updates.post_return_date = '';
                    setFormData({ ...formData, ...updates });
                  }}
                  testId="post-travel-abroad"
                />

                {/* Date Returned to UK — only shown if travel was outside UK */}
                {formData.post_travel_abroad === true && (
                  <div className="space-y-1 pl-3 border-l-2 border-amber-500/30">
                    <Label className="text-amber-400 text-xs font-medium">Date Returned to UK</Label>
                    <DateInput contextDate={dateContext} value={formData.post_return_date || ''} onChange={(e) => setFormData({ ...formData, post_return_date: e.target.value })}
                      className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid="post-return-date-input" />
                  </div>
                )}

                {/* Travel route */}
                <div className="pl-3 border-l-2 border-amber-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Plane className="h-3 w-3 text-amber-400" />
                    <p className="text-[10px] text-slate-500">Travel route</p>
                  </div>
                  <input value={formData.post_travel_route || ''} onChange={(e) => setFormData({ ...formData, post_travel_route: e.target.value })} placeholder="e.g., Singapore to Heathrow, drive home"
                    className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid="post-travel-route" />
                </div>
              </>
            )}

            {/* Q2: Hotel */}
            <YesNoToggle
              label="Did you stay in a hotel after leaving?"
              value={formData.post_had_hotel}
              onChange={(val) => {
                const updates = { post_had_hotel: val };
                if (!val) {
                  updates.post_hotel_abroad = undefined;
                  updates.post_hotel_name = '';
                  updates.post_hotel_location = '';
                  updates.post_hotel_checkin = '';
                  updates.post_hotel_checkout = '';
                }
                setFormData({ ...formData, ...updates });
              }}
              testId="post-hotel"
            />

            {formData.post_had_hotel === true && (
              <HotelFields prefix="post" formData={formData} setFormData={setFormData} dateContext={dateContext} />
            )}
          </div>

          {/* Info note for "outside" status with no travel */}
          {status === 'outside' && formData.pre_had_travel !== true && formData.post_had_travel !== true && !formData.no_uk_departure && !formData.no_uk_return && (
            <p className="text-[11px] text-slate-500 px-1">Vessel dates will be used as your UK departure/return dates for SED calculation.</p>
          )}
        </>
      )}
    </>
  );
};

export default WorkTripFields;
